import React, { useState } from 'react';
import { Button, Form, Accordion, Badge } from 'react-bootstrap';
import { useHasPermission, PERMISSIONS, PERMISSION_CATEGORIES } from '../../utils/permissions';

// Permission categories with display names and descriptions
const PERMISSION_CATEGORY_INFO = {
    [PERMISSION_CATEGORIES.PRODUCTS]: {
        displayName: 'Products Management',
        description: 'Manage product catalog and information',
        icon: '📦'
    },
    [PERMISSION_CATEGORIES.STAFF]: {
        displayName: 'Staff Management', 
        description: 'Manage staff members and their information',
        icon: '👥'
    },
    [PERMISSION_CATEGORIES.USER_MANAGEMENT]: {
        displayName: 'User & Role Management',
        description: 'Manage user accounts, roles and permissions',
        icon: '🔐'
    },
    [PERMISSION_CATEGORIES.BROKERS]: {
        displayName: 'Brokers',
        description: 'Manage broker information and relationships',
        icon: '🤝'
    },
    [PERMISSION_CATEGORIES.VENDORS]: {
        displayName: 'Vendors',
        description: 'Manage vendor information and relationships',
        icon: '🏢'
    },
    [PERMISSION_CATEGORIES.BUYERS]: {
        displayName: 'Buyers',
        description: 'Manage buyer information and relationships',
        icon: '🛒'
    },
    [PERMISSION_CATEGORIES.SALES_ORDERS]: {
        displayName: 'Sales Orders',
        description: 'Manage sales orders and customer transactions',
        icon: '📋'
    },
    [PERMISSION_CATEGORIES.PURCHASE_ORDERS]: {
        displayName: 'Purchase Orders',
        description: 'Manage purchase orders and procurement',
        icon: '📝'
    },
    [PERMISSION_CATEGORIES.GRN]: {
        displayName: 'Goods Receipt Note (GRN)',
        description: 'Manage goods receipt and inventory receiving',
        icon: '📥'
    },
    [PERMISSION_CATEGORIES.QUALITY_CHECK]: {
        displayName: 'Quality Check',
        description: 'Manage quality control processes',
        icon: '✅'
    },
    [PERMISSION_CATEGORIES.UNLOADING]: {
        displayName: 'Unloading',
        description: 'Manage unloading operations',
        icon: '🚛'
    },
    [PERMISSION_CATEGORIES.RETURNS]: {
        displayName: 'Return Notes',
        description: 'Manage return processes and documentation',
        icon: '↩️'
    },
    [PERMISSION_CATEGORIES.PURCHASE_DOCUMENTS]: {
        displayName: 'Purchase Documents',
        description: 'Manage purchase-related documentation',
        icon: '📄'
    },
    [PERMISSION_CATEGORIES.PURCHASE_PAYMENTS]: {
        displayName: 'Purchase Payments',
        description: 'Manage purchase order payments',
        icon: '💳'
    },
    [PERMISSION_CATEGORIES.PURCHASE_REQUESTS]: {
        displayName: 'Purchase Requests',
        description: 'Manage purchase requests and approvals',
        icon: '📋'
    },
    [PERMISSION_CATEGORIES.QUOTATIONS]: {
        displayName: 'Quotations',
        description: 'Manage quotations and pricing',
        icon: '💰'
    },
    [PERMISSION_CATEGORIES.BOM]: {
        displayName: 'Bill of Materials (BOM)',
        description: 'Manage product recipes and formulations',
        icon: '📊'
    },
    [PERMISSION_CATEGORIES.PRODUCTION_BATCH]: {
        displayName: 'Production Batch',
        description: 'Manage production batches and manufacturing',
        icon: '⚙️'
    },
    [PERMISSION_CATEGORIES.PRODUCTION_REQUEST]: {
        displayName: 'Production Request',
        description: 'Manage production planning and requests',
        icon: '🏭'
    },
    [PERMISSION_CATEGORIES.MACHINES]: {
        displayName: 'Machines',
        description: 'Manage machine information and maintenance',
        icon: '🔧'
    },
    [PERMISSION_CATEGORIES.PLANTS]: {
        displayName: 'Plants',
        description: 'Manage plant locations and facilities',
        icon: '🏭'
    },
    [PERMISSION_CATEGORIES.WAREHOUSE]: {
        displayName: 'Warehouse',
        description: 'Manage warehouse locations and inventory',
        icon: '🏪'
    },
    [PERMISSION_CATEGORIES.PACKAGING_MATERIAL]: {
        displayName: 'Packaging Material',
        description: 'Manage packaging materials and supplies',
        icon: '📦'
    },
    [PERMISSION_CATEGORIES.SETTINGS]: {
        displayName: 'SETTINGS',
        description: 'Manage company address, branding and payment terms',
        icon: '⚙️'
    }
};

// Helper function to get permission display name
const getPermissionDisplayName = (permission) => {
    const displayNames = {
        // Products
        'add_products': 'Add Products',
        'edit_products': 'Edit Products', 
        'view_products': 'View Products',
        
        // Staff
        'add_staff': 'Add Staff',
        'edit_staff': 'Edit Staff',
        
        // User Management
        'add_role': 'Add Role',
        'edit_permissions': 'Edit Permissions',
        'user_management': 'User Management',
        
        // Brokers
        'view_brokers': 'View Brokers',
        'add_brokers': 'Add Brokers',
        'edit_brokers': 'Edit Brokers',
        
        // Vendors
        'view_vendors': 'View Vendors',
        'add_vendors': 'Add Vendors',
        'edit_vendors': 'Edit Vendors',
        
        // Buyers
        'view_buyers': 'View Buyers',
        'add_buyers': 'Add Buyers',
        'edit_buyers': 'Edit Buyers',
        
        // Sales Orders
        'so_view': 'View Sales Orders',
        'so_create': 'Create Sales Orders',
        'so_edit': 'Edit Sales Orders',
        'so_delete': 'Delete Sales Orders',

        // In-House Sales Orders
        'so_inhouse_view': 'View In-House Sales Orders',
        'so_inhouse_create': 'Create In-House Sales Orders',
        'so_inhouse_edit': 'Edit In-House Sales Orders',
        'so_inhouse_delete': 'Delete In-House Sales Orders',
        
        // Purchase Orders
        'po_view': 'View Purchase Orders',
        'po_create': 'Create Purchase Orders',
        'po_edit': 'Edit Purchase Orders',
        'po_delete': 'Delete Purchase Orders',
        
        // GRN
        'grn_create': 'Create GRN',
        
        // Quality Check
        'quality_check_create': 'Create Quality Check',
        
        // Unloading
        'unloading_entry_add': 'Add Unloading Entry',
        
        // Returns
        'return_note_create': 'Create Return Note',
        
        // Purchase Documents
        'po_doc_add': 'Add PO Documents',
        
        // Purchase Payments
        'po_payment_add': 'Add PO Payments',
        
        // Purchase Requests
        'po_request_add': 'Add Purchase Requests',
        'po_request_edit': 'Edit Purchase Requests',
        'po_request_view': 'View Purchase Requests',
        
        // Quotations
        'quotation_view': 'View Quotations',
        'quotation_create': 'Create Quotations',
        'quotation_edit': 'Edit Quotations',
        
        // BOM
        'bom_create': 'Create BOM',
        'bom_view': 'View BOM',
        'bom_edit': 'Edit BOM',
        
        // Production Batch
        'production_batch_add': 'Add Production Batch',
        'production_batch_view': 'View Production Batch',
        'production_batch_edit': 'Edit Production Batch',
        
        // Production Request
        'production_request_create': 'Create Production Request',
        'production_request_view': 'View Production Request',
        'production_request_edit': 'Edit Production Request',
        
        // Machines
        'machine_master_add': 'Add Machines',
        'machine_master_edit': 'Edit Machines',
        'machine_master_view': 'View Machines',
        
        // Plants
        'plant_master_add': 'Add Plants',
        'plant_master_view': 'View Plants',
        'plant_master_edit': 'Edit Plants',
        
        // Warehouse
        'warehouse_add': 'Add Warehouse',
        'warehouse_view': 'View Warehouse',
        'warehouse_edit': 'Edit Warehouse',
        
        // Packaging Material
        'packaging_material_add': 'Add Packaging Material',
        'packaging_material_view': 'View Packaging Material',
        'packaging_material_edit': 'Edit Packaging Material'
    };
    
    return displayNames[permission] || permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Helper function to group permissions by category
const groupPermissionsByCategory = (permissions) => {
    const grouped = {};
    
    permissions.forEach(permission => {
        let category = 'other';
        
        // Map permission names to categories
        if (permission.includes('product')) category = PERMISSION_CATEGORIES.PRODUCTS;
        else if (permission.includes('staff')) category = PERMISSION_CATEGORIES.STAFF;
        else if (permission.includes('role') || permission.includes('user_management') || permission.includes('permissions')) category = PERMISSION_CATEGORIES.USER_MANAGEMENT;
        else if (permission.includes('broker')) category = PERMISSION_CATEGORIES.BROKERS;
        else if (permission.includes('vendor')) category = PERMISSION_CATEGORIES.VENDORS;
        else if (permission.includes('buyer')) category = PERMISSION_CATEGORIES.BUYERS;
        else if (permission.includes('so_') || permission.includes('dispatch')) category = PERMISSION_CATEGORIES.SALES_ORDERS;
        else if (permission.includes('po_') && !permission.includes('request')) category = PERMISSION_CATEGORIES.PURCHASE_ORDERS;
        else if (permission.includes('grn_')) category = PERMISSION_CATEGORIES.GRN;
        else if (permission.includes('quality_check')) category = PERMISSION_CATEGORIES.QUALITY_CHECK;
        else if (permission.includes('unloading')) category = PERMISSION_CATEGORIES.UNLOADING;
        else if (permission.includes('return_note')) category = PERMISSION_CATEGORIES.RETURNS;
        else if (permission.includes('po_doc')) category = PERMISSION_CATEGORIES.PURCHASE_DOCUMENTS;
        else if (permission.includes('po_payment')) category = PERMISSION_CATEGORIES.PURCHASE_PAYMENTS;
        else if (permission.includes('po_request') || permission.startsWith('purchase_request')) category = PERMISSION_CATEGORIES.PURCHASE_REQUESTS;
        else if (permission.includes('quotation')) category = PERMISSION_CATEGORIES.QUOTATIONS;
        else if (permission.includes('bom_')) category = PERMISSION_CATEGORIES.BOM;
        else if (permission.includes('production_batch')) category = PERMISSION_CATEGORIES.PRODUCTION_BATCH;
        else if (permission.includes('production_request')) category = PERMISSION_CATEGORIES.PRODUCTION_REQUEST;
        else if (permission.includes('machine')) category = PERMISSION_CATEGORIES.MACHINES;
        else if (permission.includes('plant')) category = PERMISSION_CATEGORIES.PLANTS;
        else if (permission.includes('warehouse')) category = PERMISSION_CATEGORIES.WAREHOUSE;
        else if (permission.includes('packaging_material')) category = PERMISSION_CATEGORIES.PACKAGING_MATERIAL;
        else if (permission.includes('settings')) category = PERMISSION_CATEGORIES.SETTINGS;
        
        if (!grouped[category]) {
            grouped[category] = [];
        }
        grouped[category].push(permission);
    });
    
    return grouped;
};

export default function RoleTabContent({ role, permissions, onUpdate }) {
    const [rolePermissions, setRolePermissions] = useState(new Set(role.permissions || []));
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategories] = useState(new Set());
    const [showOnlySelected, setShowOnlySelected] = useState(false);
    
    // Permission to manage permissions
    const canEditPermissions = useHasPermission(PERMISSIONS.EDIT_PERMISSIONS);
    
    // Group permissions by category
    const groupedPermissions = groupPermissionsByCategory(permissions);
    
    // Filter permissions based on search and category selection
    const filteredGroupedPermissions = React.useMemo(() => {
        const filtered = {};
        
        Object.entries(groupedPermissions).forEach(([category, perms]) => {
            if (selectedCategories.size > 0 && !selectedCategories.has(category)) {
                return;
            }
            
            const filteredPerms = perms.filter(perm => {
                const matchesSearch = searchTerm === '' || 
                    perm.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    getPermissionDisplayName(perm).toLowerCase().includes(searchTerm.toLowerCase());
                
                const matchesSelected = !showOnlySelected || rolePermissions.has(perm);
                
                return matchesSearch && matchesSelected;
            });
            
            if (filteredPerms.length > 0) {
                filtered[category] = filteredPerms;
            }
        });
        
        return filtered;
    }, [groupedPermissions, searchTerm, selectedCategories, showOnlySelected, rolePermissions]);

    const togglePermission = (perm) => {
        if (!canEditPermissions) return;
        
        const newPerms = new Set(rolePermissions);
        newPerms.has(perm) ? newPerms.delete(perm) : newPerms.add(perm);
        setRolePermissions(newPerms);
    };
    
    const toggleCategoryPermissions = (category, perms) => {
        if (!canEditPermissions) return;
        
        const newPerms = new Set(rolePermissions);
        const allSelected = perms.every(perm => rolePermissions.has(perm));
        
        if (allSelected) {
            // Remove all permissions in this category
            perms.forEach(perm => newPerms.delete(perm));
        } else {
            // Add all permissions in this category
            perms.forEach(perm => newPerms.add(perm));
        }
        
        setRolePermissions(newPerms);
    };
    
    const selectAllPermissions = () => {
        if (!canEditPermissions) return;
        setRolePermissions(new Set(permissions));
    };
    
    const clearAllPermissions = () => {
        if (!canEditPermissions) return;
        setRolePermissions(new Set());
    };

    const handleSubmit = () => {
        if (!canEditPermissions) return;
        onUpdate(role._id, Array.from(rolePermissions));
    };
    


    const totalSelected = rolePermissions.size;
    const totalAvailable = permissions.length;

    return (
        <div className="tab-pane h-100 fade active show" id={`tab-thread-${role._id}`} role="tabpanel">
            <div className="d-flex flex-column h-100">
                {/* Header */}
                <div className="card-header p-3 p-md-4 d-flex flex-between-center border-bottom">
                    <div className="d-flex align-items-center">
                        <button
                            className="btn ps-0 pe-2 text-body-tertiary d-sm-none"
                            onClick={() => {
                                const sidebar = document.getElementById('chat-sidebar');
                                if (window.innerWidth < 576 && sidebar && !sidebar.classList.contains('show')) {
                                    sidebar.classList.add('show');
                                }
                            }}
                        >
                            <span className="fa-solid fa-chevron-left"></span>
                        </button>
                        <div className="d-flex flex-column">
                            <h5 className="mb-1 text-body-emphasis">{role.name}</h5>
                            <small className="text-body-secondary">
                                {totalSelected} of {totalAvailable} permissions selected
                            </small>
                        </div>
                    </div>
                    <Badge bg="primary" className="fs-7">
                        Permissions
                    </Badge>
                </div>

                {/* Search and Filter Controls */}
                <div className="card-body border-bottom p-3">
                    <div className="row">
                        <div className="col-md-6">
                            <Form.Control
                                type="search"
                                placeholder="Search permissions..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                size="sm"
                            />
                        </div>
                        <div className="col-md-6">
                            <div className="d-flex gap-2 flex-wrap">
                                <Form.Check
                                    type="switch"
                                    id="show-only-selected"
                                    label="Show only selected"
                                    checked={showOnlySelected}
                                    onChange={(e) => setShowOnlySelected(e.target.checked)}
                                    size="sm"
                                />
                            </div>
                        </div>
                    </div>
                    
                    {/* Quick Actions */}
                    {canEditPermissions && (
                        <div className="d-flex gap-2 mt-3">
                            <Button variant="outline-success" size="sm" onClick={selectAllPermissions}>
                                Select All
                            </Button>
                            <Button variant="outline-danger" size="sm" onClick={clearAllPermissions}>
                                Clear All
                            </Button>
                        </div>
                    )}
                </div>

                {/* Permissions List */}
                <div className={`flex-grow-1 overflow-auto p-3 scrollbar chat-content-body-${role._id}`}>
                    <Accordion defaultActiveKey={Object.keys(filteredGroupedPermissions).slice(0, 3)}>
                        {Object.entries(filteredGroupedPermissions).map(([category, perms]) => {
                            const categoryInfo = PERMISSION_CATEGORY_INFO[category] || {
                                displayName: category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                                description: `Manage ${category.replace(/_/g, ' ')} related operations`,
                                icon: '📋'
                            };
                            
                            const selectedInCategory = perms.filter(perm => rolePermissions.has(perm)).length;
                            const allCategorySelected = perms.length > 0 && perms.every(perm => rolePermissions.has(perm));
                            const someCategorySelected = perms.some(perm => rolePermissions.has(perm));
                            
                            return (
                                <Accordion.Item eventKey={category} key={category} className="mb-2">
                                    <Accordion.Header>
                                        <div className="d-flex align-items-center justify-content-between w-100 me-2">
                                            <div className="d-flex align-items-center">
                                                <span className="me-2" style={{ fontSize: '1.2em' }}>
                                                    {categoryInfo.icon}
                                                </span>
                                                <div>
                                                    <div className="fw-semibold">{categoryInfo.displayName}</div>
                                                    <small className="text-muted">{categoryInfo.description}</small>
                                                </div>
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <Badge 
                                                    bg={allCategorySelected ? 'success' : someCategorySelected ? 'warning' : 'secondary'}
                                                    className="fs-8"
                                                >
                                                    {selectedInCategory}/{perms.length}
                                                </Badge>
                                                {canEditPermissions && (
                                                    <Form.Check
                                                        type="checkbox"
                                                        checked={allCategorySelected}
                                                        ref={(ref) => {
                                                            if (ref) ref.indeterminate = someCategorySelected && !allCategorySelected;
                                                        }}
                                                        onChange={() => toggleCategoryPermissions(category, perms)}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <div className="row">
                                            {perms.map((perm) => (
                                                <div key={perm} className="col-md-6 col-lg-4 mb-2">
                                                    <div className="border rounded p-2 h-100">
                                                        <Form.Check
                                                            type="switch"
                                                            id={`perm-${role._id}-${perm}`}
                                                            label={getPermissionDisplayName(perm)}
                                                            checked={rolePermissions.has(perm)}
                                                            onChange={() => togglePermission(perm)}
                                                            disabled={!canEditPermissions}
                                                        />
                                                        <small className="text-muted d-block mt-1">
                                                            {perm}
                                                        </small>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </Accordion.Body>
                                </Accordion.Item>
                            );
                        })}
                    </Accordion>
                    
                    {Object.keys(filteredGroupedPermissions).length === 0 && (
                        <div className="text-center py-5">
                            <div className="text-muted">
                                <i className="fas fa-search fa-2x mb-3"></i>
                                <p>No permissions found matching your criteria</p>
                                <small>Try adjusting your search or filter settings</small>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                {canEditPermissions && (
                    <div className="card-footer border-top p-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                                {totalSelected} permissions selected
                            </small>
                            <Button variant="primary" onClick={handleSubmit}>
                                <i className="fas fa-save me-2"></i>
                                Update Permissions
                            </Button>
                        </div>
                    </div>
                )}
                
                {!canEditPermissions && (
                    <div className="card-footer border-top p-3">
                        <div className="alert alert-info mb-0 py-2">
                            <i className="fas fa-info-circle me-2"></i>
                            <small>You don't have permission to edit role permissions</small>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
